import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Select,
  InputLabel,
  FormControl, MenuItem,
  Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import DetailsPageLowerLayer from '../../../Components/DetailsPageLowerLayer/DetailsPageLowerLayer';
import Link from "@mui/material/Link";
import TopSection from '../../../CommonComponents/TopSection';
import { fetchAllReports, revokeUserAccess } from "../../../services/UserRole/ReportsService";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
const INPUT_WIDTH = 450;

const normalizeApiResponse = (apiData) => {
  const rows = [];
  apiData.forEach((user, userIndex) => {
    const regions = user.regions || [];
    const plants = user.allPlants || [];

    // If user has no access at all
    // if (regions.length === 0 && plants.length === 0) {
    //   rows.push({
    //     id: `${userIndex}-empty`,
    //     userName: user.userName,
    //     userEmail: user.userEmail,
    //     region: '',
    //     mrPlant: '',
    //     restrictedAccess: user.approvalWorkflows || [],
    //     accessEnabledDate: '',
    //     ndaURL: '',
    //     requestedFor: ''
    //   });
    //   return;
    // }
    for (let i = 0; i < regions.length; i++) {
      rows.push({
        id: `${userIndex}-${i}`,
        userName: user.userName,
        userEmail: user.userEmail,
        region: regions[i],
        mrPlant: plants[i],
        restrictedAccess: user.approvalWorkflows || [],
        accessEnabledDate: user.accessEnabledDate,
        ndaURL: user.ndaUrl,
        //requestedFor: ''
      });
    }
  });
  return rows;
};

const downloadCsv = (rows) => {
  const headers = ['User Name', 'User Email', 'Region', 'MR Plant', 'Restricted Access', 'Access Enabled Date', 'NDA URL', 'Requested For'];
  const csvRows = [headers.join(',')];
  rows.forEach(r => {
    const line = [r.userName, r.userEmail, r.region, r.mrPlant, Array.isArray(r.restrictedAccess) ? r.restrictedAccess.join('; ') : r.restrictedAccess, r.accessEnabledDate, r.ndaURL, r.requestedFor];
    csvRows.push(line.map(v => `"${String(v || '')}"`).join(','));
  });
  const csv = csvRows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute('download', `user-access-report_${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const NdaLinkRenderer = (props) => {
  console.log("Rendering NDA Link with URL:", props.value);
  const url = props.value;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: "#1976d2",
        textDecoration: "underline",
        cursor: "pointer"
      }}
      onClick={(e) => e.stopPropagation()}
    >
     {url}
    </a>
  );
};

const UserAccessReport = () => {
  const breadcrumbArray = ["User Access Management", "Reports"];
  const [selectedRows, setSelectedRows] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoadingReport(true);
        const response = await fetchAllReports();
        setReportRows(normalizeApiResponse(response));
      } catch (error) {
        console.error('Failed to fetch user access reports', error);
      } finally {
        setLoadingReport(false);
      }
    };
    fetchReports();
  }, []);

  const handleRevoke = useCallback(async () => {
    const payload = {
      userEmail: selectedRows[0].userEmail,
      allPlants: [selectedRows[0].mrPlant]
    };
    try {
      const response = await revokeUserAccess(payload);

      if (response.statusCode === 200) {
        setSnackbar({
          open: true,
          message: response.message || "Access revoked successfully",
          severity: "success",
        });

        setLoadingReport(true);
        const refreshedData = await fetchAllReports();
        setReportRows(normalizeApiResponse(refreshedData));
        setLoadingReport(false);
        setSelectedRows([]);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to revoke access",
        severity: "error",
      });
      console.error("Revoke access failed:", error);
    }
  }, [selectedRows]);

  const reportTab = useMemo(() => ({
    label: `Report`,
    loading: loadingReport,
    toolbar: {
      buttons: [
        {
          label: "Revoke",
          disabled: selectedRows.length !== 1,
          onClick: handleRevoke,
        },
        {
          label: "Download",
          onClick: () => downloadCsv(reportRows)
        }
      ]
    },
    columns: [
      {
        headerName: '',
        field: 'checkbox',
        minWidth: 30,
        checkboxSelection: true,
      },
      { field: 'userName', headerName: 'User Name', flex: 1 },
      { field: 'userEmail', headerName: 'User Email', flex: 1 },
      { field: 'region', headerName: 'Region', flex: 0.8 },
      { field: 'mrPlant', headerName: 'MR Plant', flex: 0.5 },
      { field: 'restrictedAccess', headerName: 'Restricted Access', flex: 1, valueGetter: (params) => (Array.isArray(params.data.restrictedAccess) ? params.data.restrictedAccess.join(', ') : params.data.restrictedAccess) },
      { field: 'accessEnabledDate', headerName: 'Access Enabled Date', flex: 1 },
      {
        field: 'ndaURL',
        headerName: 'NDA URL',
        flex: 3,
        minWidth: 350,
        sortable: false,
        cellRenderer: NdaLinkRenderer
      }
      //{ field: 'requestedFor', headerName: 'Requested For', flex: 0.5 },
    ],
    rows: reportRows,
    rowSelection: 'multiple',
    onSelectionChanged: (event) => {
      setSelectedRows(event.api.getSelectedRows());
    },
  }), [selectedRows, reportRows, loadingReport, handleRevoke]);

  const tabs = [reportTab];

  return (
    <div>
      <TopSection breadcrumbArray={breadcrumbArray} hideHeader={true} headerName={""} hideTitle={true} />
      <Paper elevation={2} sx={{ mt: 0.5, mb: 3, pt: 0.5, pb: 1.5, bgcolor: '#ddd', height: 60 }}>
        <Box mt={2} mx="auto" sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          width: INPUT_WIDTH
        }}>
          <FormControl size="small">
            <InputLabel id="application-label">Application</InputLabel>
            <Select
              labelId="application-label"
              id="application"
              value="Command Center"
              label="Application"
              disabled
              sx={{ color: "#000" }}
            >
              <MenuItem value="Command Center">
                Command Center
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>
      <DetailsPageLowerLayer tabs={tabs} />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div >
  );
};

export default UserAccessReport;